import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import QuickActionMenu from '../../components/navigation/QuickActionMenu';
import Toaster from '../../components/Toaster';
import TwoFAReminderBanner from '../../components/TwoFAReminderBanner';
import EnterpriseLeadManagement from '../admin-dashboard/components/EnterpriseLeadManagement';
import ContractManagement from '../admin-dashboard/components/ContractManagement';
import CostTrackingChart from '../admin-dashboard/components/CostTrackingChart';
import OCRMetricsPanel from '../admin-dashboard/components/OCRMetricsPanel';
import EmailTemplateEditor from '../admin-dashboard/components/EmailTemplateEditor';
import ComplianceDashboard from '../admin-dashboard/components/ComplianceDashboard';
import PrivacyRequestsPanel from '../admin-dashboard/components/PrivacyRequestsPanel';
import EncryptionKeyPanel from '../admin-dashboard/components/EncryptionKeyPanel';
import VendorRiskPanel from '../admin-dashboard/components/VendorRiskPanel';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const tabs = [
  { id: 'enterprise-leads', label: 'Enterprise Leads', icon: 'Building' },
  { id: 'contracts', label: 'Contract Management', icon: 'FileText' },
  { id: 'costs', label: 'Cost Tracking', icon: 'DollarSign' },
  { id: 'ocr-metrics', label: 'OCR Metrics', icon: 'ScanText' },
  { id: 'email-templates', label: 'Email Templates', icon: 'Mail' },
  { id: 'compliance', label: 'Compliance Dashboard', icon: 'Shield' },
  { id: 'privacy-requests', label: 'Privacy Requests', icon: 'Lock' },
  // { id: 'encryption-keys', label: 'Encryption Keys', icon: 'Key' },
  // { id: 'vendor-risk', label: 'Vendor Risk', icon: 'Building2' },
];

const AdminBusinessManagement = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'enterprise-leads');

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" />
      <TwoFAReminderBanner />
      <Sidebar userRole="admin" />
      <QuickActionMenu userRole="admin" />
      <div className="lg:ml-60 min-h-screen">
        <header className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  const isCollapsed = document.body.classList.toggle('sidebar-collapsed');
                  localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Toggle sidebar"
              >
                <Icon name="PanelLeft" size={24} />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground">
                  Business Management
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Enterprise leads, contracts, costs, compliance and platform tools
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter userRole="admin" />
              <div className="hidden md:flex items-center gap-3 pl-3 border-l border-border">
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">Admin User</p>
                  <p className="text-xs text-muted-foreground">System Administrator</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  <Icon name="Shield" size={20} color="#FFFFFF" />
                </div>
                <button
                  onClick={async () => { await signOut(); navigate('/login'); }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  <Icon name="LogOut" size={16} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="border-b border-border overflow-x-auto">
              <div className="flex min-w-max">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 md:px-6 py-4 text-sm font-medium transition-smooth border-b-2 flex-shrink-0 ${
                      activeTab === tab.id
                        ? 'border-primary text-primary bg-primary/5'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon name={tab.icon} size={18} />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="p-4 md:p-6">
              {activeTab === 'enterprise-leads' && <EnterpriseLeadManagement />}
              {activeTab === 'contracts' && <ContractManagement />}
              {activeTab === 'costs' && <CostTrackingChart />}
              {activeTab === 'ocr-metrics' && <OCRMetricsPanel />}
              {activeTab === 'email-templates' && <EmailTemplateEditor />}
              {activeTab === 'compliance' && <ComplianceDashboard />}
              {activeTab === 'privacy-requests' && <PrivacyRequestsPanel />}
              {activeTab === 'encryption-keys' && <EncryptionKeyPanel />}
              {activeTab === 'vendor-risk' && <VendorRiskPanel />}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminBusinessManagement;
